import { CommandLineIcon, XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Card,
  CardBody,
  Chip,
  Badge,
  Divider,
} from '@nextui-org/react';
import React, { useState, useMemo } from 'react';

import { ServerUUID } from '@common/types';
import { useTerminalConnectionPool } from '@portal/context/TerminalConnectionPool';

import { TypographyUI } from './Typography.ui';

interface TerminalIconProps {
  className?: string;
}

export const TerminalIconUI = ({ className = '' }: TerminalIconProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { connectionPool, resumeConnection, closeConnection } = useTerminalConnectionPool();

  // Convert connectionPool Map to array for rendering (memoized to prevent unnecessary re-renders)
  const connections = useMemo(() => Array.from(connectionPool.values()), [connectionPool]);
  const connectedCount = connections.length;

  const handleResume = (serverUUID: ServerUUID) => {
    resumeConnection(serverUUID);
    setIsPopoverOpen(false);
  };

  const handleClose = (serverUUID: ServerUUID) => {
    closeConnection(serverUUID);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Error';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Unknown';
    }
  };

  return (
    <Popover placement='bottom-end' isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger>
        <div
          className={`relative cursor-pointer w-10 h-10 min-w-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-all duration-200 shadow-sm hover:shadow-md ${className}`}
          title='Active Terminals'
        >
          <CommandLineIcon className='h-5 w-5 text-emerald-600' />
          {connectedCount > 0 && (
            <Badge
              content={connectedCount > 99 ? '99+' : connectedCount}
              color='success'
              size='sm'
              classNames={{
                badge: 'text-white',
              }}
              className='-top-1 -right-1'
            >
              <div></div>
            </Badge>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className='w-[36rem] p-0'>
        {/* Header */}
        <div className='w-full px-3 py-2 border-b border-gray-100'>
          <div className='w-full flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <div className='w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center'>
                <CommandLineIcon className='h-4 w-4 text-white' />
              </div>
              <div>
                <TypographyUI variant='h6' color='blue-gray' className='font-bold'>
                  Active Terminals
                </TypographyUI>
                <TypographyUI variant='caption' color='gray' className='-mt-1'>
                  {connectedCount} {connectedCount === 1 ? 'connection' : 'connections'}
                </TypographyUI>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className='w-full px-3 py-2'>
          <div className='space-y-2'>
            {connections.length === 0 ? (
              <div className='w-full flex items-center justify-center gap-3 pt-2'>
                <div className='w-8 h-8 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center'>
                  <CommandLineIcon className='h-4 w-4 text-emerald-400' />
                </div>
                <div>
                  <TypographyUI variant='caption' color='gray' className='font-medium'>
                    No active terminals
                  </TypographyUI>
                  <TypographyUI variant='caption' color='gray' className='opacity-70'>
                    Open a server terminal to get started
                  </TypographyUI>
                </div>
              </div>
            ) : (
              connections.map(connection => (
                <Card
                  key={connection.serverUUID}
                  className='w-full border border-gray-100 hover:border-emerald-200 transition-colors'
                  shadow='sm'
                >
                  <CardBody className='p-2'>
                    <div className='w-full'>
                      <div className='flex justify-between items-start gap-3 mb-2'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <TypographyUI className='text-gray-800 font-medium text-sm truncate'>
                              {connection.serverData.name}
                            </TypographyUI>
                            <Chip size='sm' variant='flat' color={getStatusColor(connection.status)}>
                              <span className='text-xs'>{getStatusText(connection.status)}</span>
                            </Chip>
                          </div>
                          <TypographyUI variant='caption' className='text-gray-600 truncate'>
                            {connection.serverData.sshUsername}@{connection.serverData.host}
                          </TypographyUI>
                        </div>
                        <div className='flex items-center gap-1 flex-shrink-0'>
                          <Button
                            isIconOnly
                            size='sm'
                            variant='flat'
                            color='success'
                            onPress={() => handleResume(connection.serverUUID)}
                            className='flex-shrink-0'
                            title='Open Terminal'
                          >
                            <ArrowTopRightOnSquareIcon className='h-3 w-3' />
                          </Button>
                          <Button
                            isIconOnly
                            size='sm'
                            variant='flat'
                            color='danger'
                            onPress={() => handleClose(connection.serverUUID)}
                            className='flex-shrink-0'
                            title='Close Terminal'
                          >
                            <XMarkIcon className='h-3 w-3' />
                          </Button>
                        </div>
                      </div>
                      {connection.serverData.clusterTag && (
                        <>
                          <Divider className='my-2' />
                          <div className='flex items-center gap-2 text-xs text-gray-500'>
                            <div className='flex items-center gap-1'>
                              <div className='w-1.5 h-1.5 rounded-full bg-blue-400'></div>
                              <span>{connection.serverData.clusterTag}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
